import { brokerSwapRates } from '../data';

const pipValues: Record<string, number> = {
  EURUSD: 10,
  GBPJPY: 7.5,
  USDTRY: 8,
};

const pairNames = ['EURUSD', 'GBPJPY', 'USDTRY'] as const;
const pairLabels: Record<string, string> = {
  EURUSD: 'Major',
  GBPJPY: 'Cross',
  USDTRY: 'Exotic',
};
const pairAccents: Record<string, string> = {
  EURUSD: 'accent',
  GBPJPY: 'secondary',
  USDTRY: 'primary',
};

function fmtAbs(n: number): string {
  const abs = Math.round(Math.abs(n));
  if (abs >= 1000000) return `$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `$${abs.toLocaleString()}`;
  return `$${abs}`;
}

function fmtSigned(n: number): string {
  const sign = n < 0 ? '-' : n > 0 ? '+' : '';
  return `${sign}$${Math.round(Math.abs(n)).toLocaleString()}`;
}

// Pre‑compute annual costs per broker × pair (worst direction, 1 lot, 250 days)
type AnnualRow = {
  broker: string;
  type: string;
  markup: string;
  philosophy: string;
  pairCosts: Record<string, { longRate: number; shortRate: number; worstAnnual: number; worstDir: 'long' | 'short'; dailyWorst: number }>;
};

const annualRows: AnnualRow[] = brokerSwapRates.map((b) => {
  const pairCosts: AnnualRow['pairCosts'] = {};
  for (const pair of pairNames) {
    const pipVal = pipValues[pair] ?? 10;
    const rates = b.pairs[pair];
    const longAnnual = rates.long * pipVal * 250;
    const shortAnnual = rates.short * pipVal * 250;
    const absLong = Math.abs(longAnnual);
    const absShort = Math.abs(shortAnnual);
    const worstAnnual = Math.max(absLong, absShort);
    const worstDir: 'long' | 'short' = absLong >= absShort ? 'long' : 'short';
    const dailyWorst = worstDir === 'long' ? Math.abs(rates.long * pipVal) : Math.abs(rates.short * pipVal);
    pairCosts[pair] = { longRate: rates.long, shortRate: rates.short, worstAnnual, worstDir, dailyWorst };
  }
  return { ...b, pairCosts };
});

// Global max for bar scaling (per pair)
const globalMax: Record<string, number> = {};
for (const pair of pairNames) {
  globalMax[pair] = Math.max(...annualRows.map((r) => r.pairCosts[pair].worstAnnual));
}

export default function BrokerSwapComparisonSection() {
  return (
    <section className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* ── Header ── */}
        <div className="max-w-[680px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.12] text-foreground-950 tracking-tight mb-4">
            Same pair, different broker — the swap gap is real
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Brokers add their own markup on top of the interbank tom-next rate. A 0.5-pip difference doesn't
            sound like much — until you multiply it by 250 trading days, 1 lot, and a carry pair like USDTRY.
            Below: three broker types, three pairs, annual cost at 1 lot.
          </p>
        </div>

        {/* ── Per‑pair comparison panels ── */}
        <div className="space-y-6 mb-10">
          {pairNames.map((pair) => {
            const accent = pairAccents[pair] ?? 'accent';
            const pipVal = pipValues[pair] ?? 10;
            const maxWorst = globalMax[pair];

            // Find best and worst broker for this pair
            let bestIdx = 0;
            let worstIdx = 0;
            annualRows.forEach((r, i) => {
              if (r.pairCosts[pair].worstAnnual < annualRows[bestIdx].pairCosts[pair].worstAnnual) bestIdx = i;
              if (r.pairCosts[pair].worstAnnual > annualRows[worstIdx].pairCosts[pair].worstAnnual) worstIdx = i;
            });
            const savings = annualRows[worstIdx].pairCosts[pair].worstAnnual - annualRows[bestIdx].pairCosts[pair].worstAnnual;

            return (
              <div key={pair} className="bg-white rounded-xl border border-background-200 overflow-hidden">
                {/* Pair header */}
                <div className={`px-4 md:px-8 py-4 border-b border-background-100 ${
                  pair === 'USDTRY' ? 'bg-primary-50/30' : ''
                }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-1 h-7 md:h-8 rounded-full flex-shrink-0 ${
                        accent === 'accent' ? 'bg-accent-500' : accent === 'secondary' ? 'bg-secondary-500' : 'bg-primary-500'
                      }`} />
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-[16px] md:text-[18px] font-[750] text-foreground-950 tracking-tight whitespace-nowrap">{pair}</span>
                          <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-wide text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                            {pairLabels[pair]}
                          </span>
                        </div>
                        <div className="text-[11px] md:text-[12px] text-foreground-500 mt-0.5 truncate">
                          Pip: ${pipVal} · 250 days × 1 lot, worst direction
                        </div>
                      </div>
                    </div>

                    {/* Annual savings badge */}
                    <div className="flex-shrink-0">
                      {savings >= 100 && (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0FDF4] border border-[#BBF7D0]">
                          <span className="text-[10px] font-bold text-[#16A34A] uppercase whitespace-nowrap">
                            Save up to {fmtAbs(savings)}/yr
                          </span>
                        </div>
                      )}
                      {savings < 100 && savings > 0 && (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50 border border-background-100">
                          <span className="text-[10px] font-bold text-foreground-500 uppercase whitespace-nowrap">
                            Save up to {fmtAbs(savings)}/yr
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop comparison grid */}
                <div className="hidden md:grid grid-cols-3 divide-x divide-background-100">
                  {annualRows.map((row, idx) => {
                    const pc = row.pairCosts[pair];
                    const isBest = idx === bestIdx;
                    const isWorst = idx === worstIdx;
                    const barPct = Math.max((pc.worstAnnual / maxWorst) * 100, 3);

                    return (
                      <div
                        key={row.broker}
                        className={`p-5 md:p-6 relative transition-colors ${
                          isBest ? 'bg-[#F0FDF4]/30' : isWorst ? 'bg-[#FEF2F2]/30' : ''
                        }`}
                      >
                        {/* Broker info */}
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[14px] font-[750] text-foreground-950">{row.broker}</span>
                            {isBest && (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0]">
                                Best
                              </span>
                            )}
                            {isWorst && (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]">
                                Costliest
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-foreground-500">{row.type}</div>
                          <div className="text-[10px] text-foreground-400 mt-0.5">{row.markup}</div>
                        </div>

                        {/* Swap rates */}
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center justify-between text-[12px]">
                            <span className="text-foreground-500">Long swap</span>
                            <span className={`font-bold tabular-nums ${pc.longRate >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                              {pc.longRate >= 0 ? '+' : ''}{pc.longRate.toFixed(2)} pips
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-[12px]">
                            <span className="text-foreground-500">Short swap</span>
                            <span className={`font-bold tabular-nums ${pc.shortRate >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                              {pc.shortRate >= 0 ? '+' : ''}{pc.shortRate.toFixed(2)} pips
                            </span>
                          </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-background-100 pt-3 mb-3">
                          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-foreground-400 mb-1">
                            Annual cost · {pc.worstDir} position
                          </div>
                        </div>

                        {/* Annual cost — BIG */}
                        <div className={`text-[28px] font-[800] leading-none tracking-tight tabular-nums ${
                          pc.worstAnnual >= 5000 ? 'text-[#DC2626]' : 'text-foreground-800'
                        }`}>
                          {fmtAbs(pc.worstAnnual)}
                        </div>

                        {/* Bar */}
                        <div className="w-full h-2 bg-background-50 rounded-full overflow-hidden mt-2 mb-1">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${
                              isBest
                                ? 'bg-[#16A34A]/40'
                                : isWorst
                                ? 'bg-[#FCA5A5]/60'
                                : 'bg-background-200'
                            }`}
                            style={{ width: `${barPct}%` }}
                          />
                        </div>
                        <div className="text-[10px] text-foreground-400">
                          {fmtAbs(pc.dailyWorst)}/day × 250 days
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-background-100">
                  {annualRows.map((row, idx) => {
                    const pc = row.pairCosts[pair];
                    const isBest = idx === bestIdx;
                    const isWorst = idx === worstIdx;

                    return (
                      <div key={row.broker} className={`p-5 ${isBest ? 'bg-[#F0FDF4]/20' : isWorst ? 'bg-[#FEF2F2]/20' : ''}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <span className="text-[14px] font-[750] text-foreground-950">{row.broker}</span>
                            <div className="text-[11px] text-foreground-500">{row.type}</div>
                          </div>
                          {isBest && (
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0]">Best</span>
                          )}
                          {isWorst && (
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]">Costliest</span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div className="text-[12px]">
                            <span className="text-foreground-500">Long: </span>
                            <span className={`font-bold ${pc.longRate >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                              {pc.longRate >= 0 ? '+' : ''}{pc.longRate.toFixed(2)}
                            </span>
                          </div>
                          <div className="text-[12px]">
                            <span className="text-foreground-500">Short: </span>
                            <span className={`font-bold ${pc.shortRate >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                              {pc.shortRate >= 0 ? '+' : ''}{pc.shortRate.toFixed(2)}
                            </span>
                          </div>
                        </div>

                        <div className={`text-[22px] font-[800] tabular-nums ${pc.worstAnnual >= 5000 ? 'text-[#DC2626]' : 'text-foreground-800'}`}>
                          {fmtAbs(pc.worstAnnual)}/yr ({pc.worstDir})
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Bottom insights ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {/* Total annual savings */}
          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400 mb-3">
              The compounding effect of broker choice
            </div>
            <p className="text-[13px] leading-relaxed text-foreground-700 mb-4">
              Across all three pairs, choosing the best broker over the worst saves{' '}
              <strong className="text-[#16A34A]">
                {fmtAbs(
                  pairNames.reduce((sum, pair) => {
                    const costs = annualRows.map((r) => r.pairCosts[pair].worstAnnual);
                    return sum + (Math.max(...costs) - Math.min(...costs));
                  }, 0),
                )}
              </strong>{' '}
              per year — just from swap rates alone. That is before factoring in spread width, commission
              structure, or execution quality. For a position trader running 3 lots on USDTRY, the annual
              broker gap alone exceeds{' '}
              <strong className="text-foreground-900">$9,000</strong>.
            </p>

            <div className="p-4 rounded-lg bg-primary-50/60 border border-primary-100">
              <p className="text-[12px] leading-relaxed text-primary-700 font-semibold">
                Most traders compare brokers on spreads. For anyone holding past 5 PM ET, swap rates are the
                bigger variable. A 0.5-pip swap markup on USDTRY costs $1,000/year at 1 lot — and most traders
                never check this number before opening an account.
              </p>
            </div>
          </div>

          {/* Broker philosophy comparison */}
          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400 mb-4">
              How each broker type handles swap
            </div>

            <div className="space-y-4">
              {annualRows.map((row) => {
                const totalAnnual = pairNames.reduce((sum, pair) => sum + row.pairCosts[pair].worstAnnual, 0);

                return (
                  <div key={row.broker} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-foreground-300 mt-1.5 flex-shrink-0" />
                    <div>
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="text-[13px] font-bold text-foreground-900">{row.broker}</span>
                        <span className="text-[10px] text-foreground-400">{row.markup}</span>
                      </div>
                      <p className="text-[12px] leading-relaxed text-foreground-600">
                        {row.philosophy}
                      </p>
                      <div className="mt-1 text-[11px] font-semibold text-foreground-400">
                        Combined annual worst-case (3 pairs, 1 lot each):{' '}
                        <span className="text-foreground-700">{fmtAbs(totalAnnual)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="mt-6 text-[12px] text-foreground-500 text-center max-w-[680px] mx-auto">
          Broker swap rates shown are representative and for comparison purposes only. Actual swap rates vary daily
          based on interbank tom-next rates and broker-specific markups. Always verify current swap rates in your
          broker's contract specifications before trading. Annual projections assume 250 trading days, 1 lot, worst
          direction — actual costs depend on position size, direction, and actual holding days.
        </p>
      </div>
    </section>
  );
}